import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Users,
  UserCheck,
  Target,
  FileText,
  ShoppingCart,
  Trophy,
  XCircle,
  IndianRupee,
  Percent,
  ThumbsUp,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import salesDashboardService from '../../../api/crm/analytics/salesDashboardService';
import { formatIndianAmount } from '../../../utils/amountFormatter';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import Button from '../../../components/common/Button/Button';
import fieldSalesService from '../../../api/crm/field-sales/fieldSalesService';
import { useAuth } from '../../../context/AuthContext';
import { USER_ROLES } from '../../../utils/constants';

const CHART_COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899'];

const AGING_LABELS = {
  '0_15': '0–15 days',
  '16_30': '16–30 days',
  '31_60': '31–60 days',
  '60_plus': '60+ days'
};

const formatNumber = (num) => new Intl.NumberFormat('en-IN').format(Math.round(num || 0));

const formatAxis = (value) => {
  const num = Number(value) || 0;
  if (num >= 10000000) return `${(num / 10000000).toFixed(1)}Cr`;
  if (num >= 100000) return `${(num / 100000).toFixed(1)}L`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return String(num);
};

const ChartCard = ({ title, subtitle, children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow-sm p-4 md:p-6 ${className}`}>
    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    {subtitle && <p className="text-xs text-gray-500 mt-0.5 mb-4">{subtitle}</p>}
    {!subtitle && <div className="mb-4" />}
    {children}
  </div>
);

const EmptyChart = ({ message = 'No data available' }) => (
  <div className="h-[260px] w-full flex items-center justify-center text-sm text-gray-400">
    {message}
  </div>
);

const ExecutiveKpiCard = ({
  label,
  kpi,
  icon: Icon,
  color = 'text-gray-600',
  bg = 'bg-gray-50',
  accent = 'bg-gray-400',
  isCurrency = false,
  isPercent = false,
  isDays = false
}) => {
  if (!kpi) return null;
  const value = kpi.current ?? 0;
  const display = isCurrency
    ? `₹${formatIndianAmount(value)}`
    : isPercent
      ? `${(value || 0).toFixed(1)}%`
      : isDays
        ? `${(value || 0).toFixed(1)}d`
        : formatNumber(value);

  return (
    <div className="group relative bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md hover:border-gray-200 transition-all duration-200 overflow-hidden">
      <div className={`absolute top-0 left-0 w-1 h-full ${accent} opacity-80`} />
      <div className="flex items-start justify-between gap-3 pl-2">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 truncate">{label}</p>
          <p className={`mt-2 text-2xl font-bold text-gray-900 leading-tight ${isCurrency ? 'text-xl' : ''}`}>
            {display}
          </p>
        </div>
        <div className={`shrink-0 p-2.5 rounded-xl ${bg} ring-1 ring-inset ring-black/5 group-hover:scale-105 transition-transform duration-200`}>
          <Icon className={`h-5 w-5 ${color}`} />
        </div>
      </div>
    </div>
  );
};

const KpiSkeleton = () => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 animate-pulse overflow-hidden">
    <div className="flex items-start justify-between gap-3 pl-2">
      <div className="flex-1 space-y-3">
        <div className="h-3 w-20 bg-gray-200 rounded" />
        <div className="h-7 w-16 bg-gray-200 rounded" />
      </div>
      <div className="h-9 w-9 bg-gray-100 rounded-xl" />
    </div>
  </div>
);

const mergeTrendSeries = (trends) => {
  if (!trends) return [];
  const dateSet = new Set();
  ['leads', 'opportunities', 'quotations', 'orders'].forEach((key) => {
    (trends[key] || []).forEach((row) => dateSet.add(row.date));
  });
  const dates = Array.from(dateSet).sort();
  const toMap = (arr) => new Map((arr || []).map((r) => [r.date, r.count || 0]));

  const leadsMap = toMap(trends.leads);
  const oppsMap = toMap(trends.opportunities);
  const quotesMap = toMap(trends.quotations);
  const ordersMap = toMap(trends.orders);
  const pipelineMap = new Map((trends.pipelineGrowth || []).map((r) => [r.date, r.value || 0]));

  return dates.map((date) => ({
    date,
    label: new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
    leads: leadsMap.get(date) || 0,
    opportunities: oppsMap.get(date) || 0,
    quotations: quotesMap.get(date) || 0,
    orders: ordersMap.get(date) || 0,
    pipelineValue: pipelineMap.get(date) || 0
  }));
};

const buildDerivedRateKpi = (numeratorKpi, denominatorKpi) => {
  const currentNumerator = numeratorKpi?.current || 0;
  const currentDenominator = denominatorKpi?.current || 0;
  const currentRate = currentDenominator > 0 ? (currentNumerator / currentDenominator) * 100 : 0;

  return { current: currentRate };
};

const SalesDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const isManager = user?.role === USER_ROLES.SALES_MANAGER || user?.role === USER_ROLES.EXPORT_MANAGER;
  const isExecutive = user?.role === USER_ROLES.SALES_EXECUTIVE || user?.role === USER_ROLES.EXPORT_EXECUTIVE || user?.role === USER_ROLES.BUSINESS_RELATIONSHIP_MANAGER || user?.role === USER_ROLES.BUSINESS_DEVELOPMENT_EXECUTIVE;
  const currentUserId = user?._id || user?.id;
  const currentUserName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name || 'Me';
  const isManagerScope = isManager && currentUserId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('last_30_days');
  const [customRange, setCustomRange] = useState({ startDate: '', endDate: '' });

  const [managers, setManagers] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [selectedManager, setSelectedManager] = useState(() => (isManagerScope ? currentUserId : 'all'));
  const [selectedExecutive, setSelectedExecutive] = useState('all');

  const [executiveKpis, setExecutiveKpis] = useState(null);
  const [salesFunnel, setSalesFunnel] = useState([]);
  const [conversionMetrics, setConversionMetrics] = useState({
    leadToOpportunity: { totalLeads: 0, qualifiedLeads: 0, convertedToOpps: 0, conversionRate: 0 },
    opportunityToQuote: { totalOpportunities: 0, quotesGenerated: 0, conversionRate: 0 },
    quoteToOrder: { quotesSent: 0, ordersPlaced: 0, conversionRate: 0 }
  });
  const [trends, setTrends] = useState(null);
  const [opportunityAnalytics, setOpportunityAnalytics] = useState(null);
  const [quotationAnalytics, setQuotationAnalytics] = useState(null);
  const [recentOpportunities, setRecentOpportunities] = useState([]);

  const [sortColumn, setSortColumn] = useState('expectedCloseDate');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    if (isExecutive || isManager) return;
    const fetchManagers = async () => {
      try {
        const managersRes = await fieldSalesService.getSalesReps({ role: 'sales_manager,export_manager' });
        if (managersRes.success) setManagers(managersRes.data);
      } catch (err) {
      }
    };
    fetchManagers();
  }, [isExecutive, isManager]);

  useEffect(() => {
    if (isExecutive) return;
    const fetchExecutives = async () => {
      try {
        const params = { role: 'sales_executive,export_executive,business_relationship_manager,business_development_executive' };
        if (isManagerScope) {
          params.managerId = currentUserId;
        } else if (selectedManager && selectedManager !== 'all') {
          params.managerId = selectedManager;
        }
        const executivesRes = await fieldSalesService.getSalesReps(params);
        if (executivesRes.success) setExecutives(executivesRes.data);
      } catch (err) {
      }
    };
    fetchExecutives();
  }, [isExecutive, isManagerScope, currentUserId, selectedManager]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError(null);
      try {
        const isCustom = Boolean(customRange.startDate && customRange.endDate);
        const finalRange = isCustom ? 'custom' : dateRange;
        const result = await salesDashboardService.getSalesDashboard(
          finalRange,
          isCustom ? customRange.startDate : '',
          isCustom ? customRange.endDate : '',
          selectedManager,
          selectedExecutive
        );

        if (result.success && result.data) {
          setExecutiveKpis(result.data.executiveKpis || null);
          setSalesFunnel(result.data.salesFunnel || []);
          setConversionMetrics(result.data.conversionMetrics || conversionMetrics);
          setTrends(result.data.trends || null);
          setOpportunityAnalytics(result.data.opportunityAnalytics || null);
          setQuotationAnalytics(result.data.quotationAnalytics || null);
          setRecentOpportunities(result.data.recentOpportunities || []);
        } else {
          setError(result.error || 'Failed to load dashboard data');
        }
      } catch (err) {
        setError('An error occurred while loading dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [dateRange, customRange.startDate, customRange.endDate, selectedManager, selectedExecutive]);

  const trendChartData = useMemo(() => mergeTrendSeries(trends), [trends]);

  // For the activity trend charts (Leads, Opportunities, Quotations, Orders):
  // when "All Time" is selected, aggregate daily points by month and show a fixed
  // 12-month calendar window ending at the current month (e.g. Jul 2025 → Jul 2026),
  // zero-filling any months that have no data.
  const activityTrendData = useMemo(() => {
    const isCustom = Boolean(customRange.startDate && customRange.endDate);
    if (isCustom || dateRange !== 'all_time') return trendChartData;

    const monthMap = new Map();
    trendChartData.forEach((row) => {
      const monthKey = (row.date || '').slice(0, 7); // YYYY-MM
      if (!monthKey) return;
      const agg = monthMap.get(monthKey) || { leads: 0, opportunities: 0, quotations: 0, orders: 0 };
      agg.leads += row.leads || 0;
      agg.opportunities += row.opportunities || 0;
      agg.quotations += row.quotations || 0;
      agg.orders += row.orders || 0;
      monthMap.set(monthKey, agg);
    });

    // Build the last 12 calendar months ending with the current month.
    const now = new Date();
    const window = [];
    for (let i = 11; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const agg = monthMap.get(monthKey) || { leads: 0, opportunities: 0, quotations: 0, orders: 0 };
      window.push({
        date: monthKey,
        label: d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }),
        ...agg
      });
    }
    return window;
  }, [trendChartData, dateRange, customRange.startDate, customRange.endDate]);

  const agingChartData = useMemo(() => {
    if (!opportunityAnalytics?.agingBuckets) return [];
    return Object.entries(opportunityAnalytics.agingBuckets).map(([key, val]) => ({
      name: AGING_LABELS[key] || key,
      count: val.count || 0,
      value: val.value || 0
    }));
  }, [opportunityAnalytics]);

  const pipelineSummary = useMemo(() => {
    const buckets = opportunityAnalytics?.agingBuckets || {};
    const totalOpen = Object.values(buckets).reduce((sum, b) => sum + (b?.count || 0), 0);
    const staleCount = buckets['60_plus']?.count || 0;
    const staleValue = buckets['60_plus']?.value || 0;
    const freshCount = buckets['0_15']?.count || 0;
    return { totalOpen, staleCount, staleValue, freshCount };
  }, [opportunityAnalytics]);

  const quoteStatusData = useMemo(() => {
    return (quotationAnalytics?.statusDistribution || []).filter((s) => s.count > 0);
  }, [quotationAnalytics]);

  const leadWinRateKpi = useMemo(
    () => buildDerivedRateKpi(executiveKpis?.wonDeals, executiveKpis?.totalLeads),
    [executiveKpis]
  );

  const leadLostRateKpi = useMemo(
    () => buildDerivedRateKpi(executiveKpis?.lostDeals, executiveKpis?.totalLeads),
    [executiveKpis]
  );

  const executiveKpiCards = useMemo(() => [
    { label: 'Total Leads', kpi: executiveKpis?.totalLeads, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50', accent: 'bg-blue-500' },
    { label: 'Qualified Leads', kpi: executiveKpis?.qualifiedLeads, icon: UserCheck, color: 'text-violet-600', bg: 'bg-violet-50', accent: 'bg-violet-500' },
    { label: 'Opportunities', kpi: executiveKpis?.opportunities, icon: Target, color: 'text-purple-600', bg: 'bg-purple-50', accent: 'bg-purple-500' },
    { label: 'Quotations', kpi: executiveKpis?.quotations, icon: FileText, color: 'text-indigo-600', bg: 'bg-indigo-50', accent: 'bg-indigo-500' },
    { label: 'Orders', kpi: executiveKpis?.orders, icon: ShoppingCart, color: 'text-cyan-600', bg: 'bg-cyan-50', accent: 'bg-cyan-500' },
    { label: 'Won Deals', kpi: executiveKpis?.wonDeals, icon: Trophy, color: 'text-emerald-600', bg: 'bg-emerald-50', accent: 'bg-emerald-500' },
    { label: 'Lost Deals', kpi: executiveKpis?.lostDeals, icon: XCircle, color: 'text-rose-600', bg: 'bg-rose-50', accent: 'bg-rose-500' },
    { label: 'Quoted Revenue', kpi: executiveKpis?.quotedRevenue, icon: IndianRupee, color: 'text-teal-600', bg: 'bg-teal-50', accent: 'bg-teal-500', isCurrency: true },
    { label: 'Win Rate', kpi: executiveKpis?.winRate, icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50', accent: 'bg-green-500', isPercent: true },
    { label: 'Lead Win Rate', kpi: leadWinRateKpi, icon: Percent, color: 'text-lime-600', bg: 'bg-lime-50', accent: 'bg-lime-500', isPercent: true },
    { label: 'Lead Lost Rate', kpi: leadLostRateKpi, icon: TrendingDown, color: 'text-orange-600', bg: 'bg-orange-50', accent: 'bg-orange-500', isPercent: true },
    { label: 'Quote Acceptance', kpi: executiveKpis?.quoteAcceptanceRate, icon: ThumbsUp, color: 'text-amber-600', bg: 'bg-amber-50', accent: 'bg-amber-500', isPercent: true }
  ], [executiveKpis, leadWinRateKpi, leadLostRateKpi]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedOpportunities = () => {
    if (!sortColumn || !recentOpportunities?.length) return recentOpportunities;
    return [...recentOpportunities].sort((a, b) => {
      let aValue;
      let bValue;
      if (sortColumn === 'opportunity') {
        aValue = (a.name || a.opportunityName || '').toLowerCase();
        bValue = (b.name || b.opportunityName || '').toLowerCase();
      } else if (sortColumn === 'lead') {
        aValue = (a.leadName || '').toLowerCase();
        bValue = (b.leadName || '').toLowerCase();
      } else if (sortColumn === 'value') {
        aValue = a.value || 0;
        bValue = b.value || 0;
      } else if (sortColumn === 'status') {
        aValue = (a.stage || '').toLowerCase();
        bValue = (b.stage || '').toLowerCase();
      } else if (sortColumn === 'expectedCloseDate') {
        aValue = new Date(a.expectedCloseDate || 0).getTime();
        bValue = new Date(b.expectedCloseDate || 0).getTime();
      } else {
        aValue = a[sortColumn] || '';
        bValue = b[sortColumn] || '';
      }
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const LoadingSkeleton = () => (
    <div className="space-y-6 px-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-6 gap-4">
        {Array.from({ length: 12 }).map((_, i) => (
          <KpiSkeleton key={i} />
        ))}
      </div>
      <div className="bg-white rounded-lg shadow-sm p-6 animate-pulse h-64" />
    </div>
  );

  const getStageColor = (stage) => {
    if (!stage) return 'bg-gray-100 text-gray-700';
    const s = stage.toLowerCase();
    if (s === 'closed_won') return 'bg-green-100 text-green-800';
    if (s === 'closed_lost') return 'bg-red-100 text-red-800';
    if (s === 'qualified') return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      <div className="flex justify-between px-4 py-6 mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Dashboard</h2>
              <p className="text-sm text-gray-600">Pipeline, conversion metrics, and opportunity tracking</p>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {!isExecutive && (
            isManager ? (
              <select
                value={selectedManager}
                onChange={(e) => {
                  const nextManager = e.target.value;
                  setSelectedManager(nextManager);
                  setSelectedExecutive(nextManager === 'all' ? 'all' : 'manager_only');
                }}
                className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5 shadow-sm"
                disabled={loading || isManager}
              >
                <option value="all">All Data</option>
                <option value={currentUserId}>{currentUserName}</option>
              </select>
            ) : (
              <select
                value={selectedManager}
                onChange={(e) => {
                  setSelectedManager(e.target.value);
                  setSelectedExecutive('all');
                }}
                className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5 shadow-sm"
                disabled={loading}
              >
                <option value="all">All Managers</option>
                {managers.map((mgr) => (
                  <option key={mgr._id} value={mgr._id}>{mgr.name}</option>
                ))}
              </select>
            )
          )}

          {!isExecutive && (
            <select
              value={selectedExecutive}
              onChange={(e) => {
                const nextExecutive = e.target.value;
                if (isManager) setSelectedManager(currentUserId);
                setSelectedExecutive(nextExecutive);
              }}
              className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5 shadow-sm"
              disabled={loading}
            >
              <option value="all">All</option>
              {(isManager || selectedManager !== 'all') && <option value="my_team">My Team</option>}
              {(isManager || selectedManager !== 'all') && <option value="manager_only">My Data</option>}
              {executives.map((exec) => (
                <option key={exec._id} value={exec._id}>{exec.name}</option>
              ))}
            </select>
          )}

          {(!customRange.startDate && !customRange.endDate) && (
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5 shadow-sm"
              disabled={loading}
            >
              <option value="all_time">All Time</option>
              <option value="today">Today</option>
              <option value="last_7_days">Last 7 Days</option>
              <option value="last_30_days">Last 30 Days</option>
              <option value="last_60_days">Last 60 Days</option>
              <option value="last_90_days">Last 90 Days</option>
            </select>
          )}

          <DateRangeFilter
            startDate={customRange.startDate}
            endDate={customRange.endDate}
            onChange={(newRange) => setCustomRange((prev) => ({ ...prev, ...newRange }))}
            allowFutureDates={false}
          />

          {(customRange.startDate || customRange.endDate) && (
            <Button
              variant="outline"
              disabled={loading}
              onClick={() => {
                setCustomRange({ startDate: '', endDate: '' });
                setDateRange('last_30_days');
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="mx-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          <p className="font-medium">Error loading dashboard</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {loading && <LoadingSkeleton />}

      {!loading && (
        <div className="px-4 space-y-6">
          {/* Section 1 — Executive KPIs */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Executive KPIs</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-6 gap-4">
              {executiveKpiCards.map((card) => (
                <ExecutiveKpiCard key={card.label} {...card} />
              ))}
            </div>
          </div>

          {/* Section 2 — Sales Funnel */}
          <ChartCard
            title="Sales Pipeline Funnel"
            subtitle="Lead → Contacted → Qualified → Opportunity → Quotation → Order → Won"
          >
            {salesFunnel.length === 0 ? (
              <EmptyChart />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
                {salesFunnel.map((stage) => (
                  <div key={stage.key || stage.stage} className="bg-gray-50 p-4 rounded-lg border-l-4 border-blue-500">
                    <p className="text-xs font-semibold text-gray-900 mb-2">{stage.stage}</p>
                    <p className="text-2xl font-bold text-gray-900">{formatNumber(stage.count)}</p>
                    <p className="text-xs text-gray-500 mt-1">₹{formatIndianAmount(stage.value || 0)}</p>
                    <div className="mt-2 space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Conversion</span>
                        <span className="font-medium text-blue-600">{stage.conversion}%</span>
                      </div>
                      {stage.dropOff > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Drop-off</span>
                          <span className="font-medium text-red-500">{stage.dropOff}%</span>
                        </div>
                      )}
                      {stage.avgDaysInStage != null && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Avg days</span>
                          <span className="font-medium">{stage.avgDaysInStage}d</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ChartCard>

          {/* Section 3 — Trend Analytics */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Trend Analytics</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ChartCard title="Leads Created">
                {activityTrendData.length === 0 ? <EmptyChart /> : (
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={activityTrendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="leads" stroke="#3b82f6" strokeWidth={2} dot={false} name="Leads" />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>

              <ChartCard title="Opportunities">
                {activityTrendData.length === 0 ? <EmptyChart /> : (
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={activityTrendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="opportunities" stroke="#10b981" strokeWidth={2} dot={false} name="Opportunities" />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>

              <ChartCard title="Quotations">
                {activityTrendData.length === 0 ? <EmptyChart /> : (
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={activityTrendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="quotations" stroke="#8b5cf6" strokeWidth={2} dot={false} name="Quotations" />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>

              <ChartCard title="Orders">
                {activityTrendData.length === 0 ? <EmptyChart /> : (
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={activityTrendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="orders" stroke="#f59e0b" strokeWidth={2} dot={false} name="Orders" />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>

              <ChartCard title="Pipeline Growth" className="lg:col-span-2">
                {trendChartData.length === 0 ? <EmptyChart /> : (
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={trendChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={formatAxis} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => [`₹${formatIndianAmount(v)}`, 'Pipeline Value']} />
                      <Area type="monotone" dataKey="pipelineValue" stroke="#3b82f6" fill="#93c5fd" fillOpacity={0.4} name="Pipeline Value" />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </div>
          </div>

          {/* Section 4 — Opportunity Analytics */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Opportunity Analytics</h3>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg shadow-sm p-5 flex flex-col">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Pipeline Value</p>
                    <p className="mt-1 text-3xl font-bold text-gray-900 leading-tight">
                      ₹{formatIndianAmount(opportunityAnalytics?.pipelineValue || 0)}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">Total value of open opportunities</p>
                  </div>
                  <div className="shrink-0 p-3 rounded-xl bg-blue-50 ring-1 ring-inset ring-blue-100">
                    <IndianRupee className="h-6 w-6 text-blue-600" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mt-5">
                  <div className="rounded-lg bg-gray-50 p-3">
                    <div className="flex items-center gap-1.5 text-gray-500">
                      <Target className="h-3.5 w-3.5" />
                      <span className="text-[11px] font-medium uppercase tracking-wide">Open Opps</span>
                    </div>
                    <p className="mt-1 text-lg font-bold text-gray-900">{formatNumber(pipelineSummary.totalOpen)}</p>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-3">
                    <div className="flex items-center gap-1.5 text-gray-500">
                      <IndianRupee className="h-3.5 w-3.5" />
                      <span className="text-[11px] font-medium uppercase tracking-wide">Avg Size</span>
                    </div>
                    <p className="mt-1 text-lg font-bold text-gray-900">₹{formatIndianAmount(opportunityAnalytics?.averageOpportunitySize || 0)}</p>
                  </div>
                  <div className="rounded-lg bg-emerald-50 p-3">
                    <div className="flex items-center gap-1.5 text-emerald-600">
                      <TrendingUp className="h-3.5 w-3.5" />
                      <span className="text-[11px] font-medium uppercase tracking-wide">Fresh (0–15d)</span>
                    </div>
                    <p className="mt-1 text-lg font-bold text-emerald-700">{formatNumber(pipelineSummary.freshCount)}</p>
                  </div>
                  <div className="rounded-lg bg-rose-50 p-3">
                    <div className="flex items-center gap-1.5 text-rose-600">
                      <TrendingDown className="h-3.5 w-3.5" />
                      <span className="text-[11px] font-medium uppercase tracking-wide">Stale (60d+)</span>
                    </div>
                    <p className="mt-1 text-lg font-bold text-rose-700">{formatNumber(pipelineSummary.staleCount)}</p>
                    <p className="text-[11px] text-rose-500 mt-0.5">₹{formatIndianAmount(pipelineSummary.staleValue)}</p>
                  </div>
                </div>
              </div>

              <ChartCard title="Opportunity Aging" subtitle="Days since conversion" className="lg:col-span-2">
                {agingChartData.length === 0 ? <EmptyChart /> : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={agingChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#3b82f6" name="Count" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              <ChartCard title="Largest Open Opportunities">
                {(opportunityAnalytics?.largestOpenOpportunities || []).length === 0 ? (
                  <EmptyChart message="No open opportunities" />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 border-b">
                          <th className="pb-2">Opportunity</th>
                          <th className="pb-2">Company</th>
                          <th className="pb-2 text-right">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {opportunityAnalytics.largestOpenOpportunities.map((opp) => (
                          <tr key={opp.id} className="border-b border-gray-50">
                            <td className="py-2 truncate max-w-[140px]" title={opp.opportunityName}>{opp.opportunityName || 'N/A'}</td>
                            <td className="py-2 truncate max-w-[120px]" title={opp.company}>{opp.company || 'N/A'}</td>
                            <td className="py-2 text-right font-medium">₹{formatIndianAmount(opp.value || 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </ChartCard>

              <ChartCard title="Upcoming Closures" subtitle="Next 30 days">
                {(opportunityAnalytics?.upcomingClosures || []).length === 0 ? (
                  <EmptyChart message="No upcoming closures" />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 border-b">
                          <th className="pb-2">Opportunity</th>
                          <th className="pb-2">Closing</th>
                          <th className="pb-2 text-right">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {opportunityAnalytics.upcomingClosures.map((opp) => (
                          <tr key={opp.id} className="border-b border-gray-50">
                            <td className="py-2 truncate max-w-[140px]" title={opp.opportunityName}>{opp.opportunityName || 'N/A'}</td>
                            <td className="py-2">
                              {opp.expectedCloseDate
                                ? new Date(opp.expectedCloseDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                                : 'N/A'}
                            </td>
                            <td className="py-2 text-right font-medium">₹{formatIndianAmount(opp.value || 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </ChartCard>
            </div>
          </div>

          {/* Section 5 — Quotation Analytics */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Quotation Analytics</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <div className="bg-white rounded-lg shadow-sm p-4">
                <p className="text-xs text-gray-500">Avg Quote Value</p>
                <p className="text-xl font-bold">₹{formatIndianAmount(quotationAnalytics?.averageQuoteValue || 0)}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-4">
                <p className="text-xs text-gray-500">Avg Discount</p>
                <p className="text-xl font-bold">{(quotationAnalytics?.averageDiscountPct || 0).toFixed(1)}%</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-4">
                <p className="text-xs text-gray-500">Expiring (7d)</p>
                <p className="text-xl font-bold text-amber-600">{formatNumber(quotationAnalytics?.expiringQuotations || 0)}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-4">
                <p className="text-xs text-gray-500">Rejected</p>
                <p className="text-xl font-bold text-red-600">{formatNumber(quotationAnalytics?.rejectedQuotations || 0)}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ChartCard title="Quotation Status Distribution">
                {quoteStatusData.length === 0 ? <EmptyChart /> : (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={quoteStatusData} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={90} label={({ status, count }) => `${status}: ${count}`}>
                        {quoteStatusData.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>

              <ChartCard title="Quote Lifecycle" subtitle="Stage-to-stage conversion rates">
                {quotationAnalytics?.lifecycle ? (
                  <div className="space-y-4">
                    {[
                      { label: 'Created', count: quotationAnalytics.lifecycle.created },
                      { label: 'Sent', count: quotationAnalytics.lifecycle.sent, rate: quotationAnalytics.lifecycle.createdToSentRate },
                      { label: 'Seen', count: quotationAnalytics.lifecycle.seen, rate: quotationAnalytics.lifecycle.sentToSeenRate },
                      { label: 'Accepted', count: quotationAnalytics.lifecycle.accepted, rate: quotationAnalytics.lifecycle.seenToAcceptedRate }
                    ].map((step, idx) => (
                      <div key={step.label}>
                        <div className="flex justify-between text-sm">
                          <span className="font-medium text-gray-700">{step.label}</span>
                          <span className="text-gray-900 font-semibold">{formatNumber(step.count)}</span>
                        </div>
                        {idx > 0 && step.rate != null && (
                          <div className="text-xs text-blue-600 mt-0.5">{step.rate.toFixed(1)}% from previous stage</div>
                        )}
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${Math.min(100, (step.count / (quotationAnalytics.lifecycle.created || 1)) * 100)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyChart />
                )}
              </ChartCard>
            </div>
          </div>

          {/* Conversion Metrics (preserved) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Lead → Opportunity</h3>
                <button onClick={() => navigate('/lead_list_filters')} className="text-sm text-blue-600 hover:text-blue-700 font-medium" type="button">View Leads</button>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between"><span className="text-sm text-gray-600">Leads</span><span className="text-lg font-bold">{formatNumber(conversionMetrics.leadToOpportunity.totalLeads)}</span></div>
                <div className="flex justify-between"><span className="text-sm text-gray-600">Qualified</span><span className="text-lg font-bold text-yellow-600">{formatNumber(conversionMetrics.leadToOpportunity.qualifiedLeads)}</span></div>
                <div className="flex justify-between"><span className="text-sm text-gray-600">Converted</span><span className="text-lg font-bold text-blue-600">{formatNumber(conversionMetrics.leadToOpportunity.convertedToOpps)}</span></div>
                <div className="w-full bg-gray-200 rounded-full h-3"><div className="bg-blue-600 h-3 rounded-full" style={{ width: `${Math.min(conversionMetrics.leadToOpportunity.conversionRate, 100)}%` }} /></div>
                <div className="text-center text-sm text-gray-600">Rate: {conversionMetrics.leadToOpportunity.conversionRate.toFixed(1)}%</div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Opportunity → Quote</h3>
                <button onClick={() => navigate('/crm/quotation-proposal/create-quotes')} className="text-sm text-blue-600 hover:text-blue-700 font-medium" type="button">View Quotes</button>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between"><span className="text-sm text-gray-600">Opportunities</span><span className="text-lg font-bold">{conversionMetrics.opportunityToQuote.totalOpportunities}</span></div>
                <div className="flex justify-between"><span className="text-sm text-gray-600">Quotes Generated</span><span className="text-lg font-bold text-green-600">{conversionMetrics.opportunityToQuote.quotesGenerated}</span></div>
                <div className="w-full bg-gray-200 rounded-full h-3"><div className="bg-green-600 h-3 rounded-full" style={{ width: `${Math.min(conversionMetrics.opportunityToQuote.conversionRate, 100)}%` }} /></div>
                <div className="text-center text-sm text-gray-600">Rate: {conversionMetrics.opportunityToQuote.conversionRate.toFixed(1)}%</div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Quote → Order</h3>
                <button onClick={() => navigate('/order_management')} className="text-sm text-blue-600 hover:text-blue-700 font-medium" type="button">View Tracking</button>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between"><span className="text-sm text-gray-600">Quotes</span><span className="text-lg font-bold">{formatNumber(conversionMetrics.quoteToOrder.quotesSent)}</span></div>
                <div className="flex justify-between"><span className="text-sm text-gray-600">Orders Placed</span><span className="text-lg font-bold text-purple-600">{formatNumber(conversionMetrics.quoteToOrder.ordersPlaced)}</span></div>
                <div className="w-full bg-gray-200 rounded-full h-3"><div className="bg-purple-600 h-3 rounded-full" style={{ width: `${Math.min(conversionMetrics.quoteToOrder.conversionRate, 100)}%` }} /></div>
                <div className="text-center text-sm text-gray-600">Rate: {conversionMetrics.quoteToOrder.conversionRate.toFixed(1)}%</div>
              </div>
            </div>
          </div>

          {/* Recent Opportunities (preserved) */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Recent</h3>
              <button onClick={() => navigate('/Opportunity-dashboard')} className="text-sm text-blue-600 hover:text-blue-700 font-medium" type="button">View All</button>
            </div>
            <div className="overflow-x-auto">
              {recentOpportunities.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200 table-fixed">
                  <thead className="bg-gray-50">
                    <tr>
                      {[
                        { key: 'opportunity', label: 'Opportunity' },
                        { key: 'lead', label: 'Lead Details' },
                        { key: 'value', label: 'Value' },
                        { key: 'status', label: 'Status' },
                        { key: 'expectedCloseDate', label: 'Closing' }
                      ].map((col) => (
                        <th key={col.key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none" onClick={() => handleSort(col.key)}>
                          <div className="flex items-center space-x-1">
                            <span>{col.label}</span>
                            {sortColumn === col.key ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />) : <ArrowUpDown className="h-4 w-4 text-gray-400" />}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getSortedOpportunities().map((opp, index) => {
                      const stageDisplay = opp.stage ? opp.stage.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : 'New';
                      const valueFormatted = `₹${formatIndianAmount(opp.value || 0)}`;
                      let expectedCloseFormatted = 'N/A';
                      if (opp.expectedCloseDate) {
                        const d = new Date(opp.expectedCloseDate);
                        if (!isNaN(d.getTime())) {
                          expectedCloseFormatted = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                        }
                      }
                      return (
                        <tr key={opp.id || opp._id || index} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900 truncate">{opp.name || opp.opportunityName || 'N/A'}</div>
                            <div className="text-xs text-gray-500">ID: {opp.opportunityId || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900 truncate">{opp.leadName || 'N/A'}</div>
                            <div className="text-sm text-gray-500 truncate">{opp.company || opp.companyName || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 text-sm">{valueFormatted}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${getStageColor(opp.stage)}`}>{stageDisplay}</span>
                          </td>
                          <td className="px-6 py-4 text-sm">{expectedCloseFormatted}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-8 text-gray-500"><p>No recent opportunities found</p></div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesDashboard;
